import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Stack, Tooltip } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import {
  Button,
  ConfirmDeleteModal,
  Icon,
  IconButton,
  Image,
  Table,
  type TableHeader,
  Typography,
  formatPhoneNumber,
} from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { spacing } from '~tokens/internal'
import { type TwilioAccountConfig } from '~types'

import { useAISettings } from '../../AISettingsContext'

import TwilioAccountForm from './TwilioAccountForm'
import './TwilioAccountSection.scss'
import { TWILIO_LOGO_URL, TwilioAccountSidCode } from './TwilioAccountSidCode'

interface TwilioAccountSectionProps {
  twilioAccountConfigs: TwilioAccountConfig[]
  hasLoaded: boolean
  onChange: () => void
  onMessage: (message: string, type: 'success' | 'error') => void
}

const getDependentPhoneLineLabels = (config: TwilioAccountConfig): string[] =>
  (config.phone_lines ?? []).map((line) => line.name || formatPhoneNumber(line.number))

const getInUseMessage = (config: TwilioAccountConfig): string =>
  `Used by ${getDependentPhoneLineLabels(config).join(', ')}. ` +
  'Move or remove these AI phone lines before modifying this Twilio account.'

const TwilioAccountSection: React.FC<TwilioAccountSectionProps> = ({
  twilioAccountConfigs,
  hasLoaded,
  onChange,
  onMessage,
}) => {
  const { selectedOrganization } = useAISettings()
  const user = useSelector((state: RootState) => state?.auth.user)

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [configToEdit, setConfigToEdit] = useState<TwilioAccountConfig | undefined>(undefined)
  const [configToDelete, setConfigToDelete] = useState<TwilioAccountConfig | null>(null)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const hasConfigs = twilioAccountConfigs.length > 0
  const showForm = isFormOpen || (hasLoaded && !hasConfigs)

  const openForm = (config?: TwilioAccountConfig): void => {
    setConfigToEdit(config)
    setIsFormOpen(true)
  }

  const closeForm = (): void => {
    setConfigToEdit(undefined)
    setIsFormOpen(false)
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!configToDelete || !selectedOrganization?.id || !user) {
      setConfigToDelete(null)
      return
    }
    setIsDeleting(true)
    try {
      await apiZodios.integrations_deleteTwilioAccountConfig({
        user_id: user.id,
        organization_identity_id: selectedOrganization.id,
        twilio_account_config_id: configToDelete.id,
      })
      onMessage('Twilio account deleted successfully', 'success')
      onChange()
    } catch (error) {
      console.error('Error deleting Twilio account:', error)
      onMessage('Failed to delete Twilio account', 'error')
    } finally {
      setIsDeleting(false)
      setConfigToDelete(null)
    }
  }

  const headers: TableHeader<TwilioAccountConfig>[] = [
    {
      id: 'name',
      label: 'Name',
      render: (config) => (
        <Box className='twilio-account-section__name'>
          <Image src={TWILIO_LOGO_URL} alt='Twilio' className='twilio-account-section__logo' />
          <Typography variant='body2'>{config.name}</Typography>
        </Box>
      ),
    },
    {
      id: 'account_sid',
      label: 'Account SID',
      render: (config) => <TwilioAccountSidCode sid={config.account_sid} />,
    },
    {
      id: 'phone_lines',
      label: 'AI Phone Lines',
      render: (config): React.ReactNode => {
        const labels = getDependentPhoneLineLabels(config)
        return (
          <Typography variant='body2' className='twilio-account-section__in-use'>
            {labels.length ? labels.join(', ') : 'None'}
          </Typography>
        )
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (config): React.ReactNode => {
        const isInUse = (config.phone_lines ?? []).length > 0
        return (
          <Stack direction='row' spacing={spacing.xxs}>
            <Tooltip title={isInUse ? getInUseMessage(config) : 'Edit Twilio account'}>
              <span>
                <IconButton
                  size='small'
                  aria-label='Edit Twilio account'
                  disabled={isInUse}
                  onClick={() => openForm(config)}
                >
                  <Icon name='edit' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isInUse ? getInUseMessage(config) : 'Delete Twilio account'}>
              <span>
                <IconButton
                  size='small'
                  color='error'
                  aria-label='Delete Twilio account'
                  disabled={isInUse}
                  onClick={() => setConfigToDelete(config)}
                >
                  <Icon name='delete' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )
      },
    },
  ]

  return (
    <Box className='twilio-account-section'>
      <Typography className='twilio-account-section__description' variant='body2'>
        Phone lines use Polimorphic's Twilio account by default. Add a Twilio sub-account to use the
        numbers, messaging services, and trunks owned by that account for SMS and voice. Accounts
        used by AI phone lines can't be edited or deleted until those phone lines are moved to
        another account.
      </Typography>
      {hasConfigs && (
        <Table
          headers={headers}
          data={twilioAccountConfigs}
          getRowClassName={(config) =>
            (config.phone_lines ?? []).length > 0
              ? 'twilio-account-section__row--in-use'
              : undefined
          }
        />
      )}
      {showForm ? (
        <TwilioAccountForm
          key={configToEdit?.id ?? 'new'}
          twilioAccountConfig={configToEdit}
          onCancel={hasConfigs ? closeForm : undefined}
          onSuccess={(message) => {
            onMessage(message, 'success')
            closeForm()
            onChange()
          }}
        />
      ) : (
        hasConfigs && (
          <Button
            className='twilio-account-section__add-another'
            variant='text'
            size='small'
            width='fit'
            startIcon={<Icon name='AddCircleOutlined' />}
            onClick={() => openForm()}
          >
            Add another
          </Button>
        )
      )}
      <ConfirmDeleteModal
        isOpen={!!configToDelete}
        onClose={() => setConfigToDelete(null)}
        onConfirm={handleConfirmDelete}
        title='Delete Twilio Account'
        itemName={configToDelete?.name}
        isLoading={isDeleting}
      />
    </Box>
  )
}

export default TwilioAccountSection
